import React, { useContext, useEffect, useState, useRef } from 'react';
import { CartContext } from '../../store/CartContext';

import { VALIDATION_SCHEMA } from '../../helpers/checkout-validation-schema';

import { getApiKeys, getDemoKeys, getPaymentBody } from '../../helpers/checkout-helpers';
import { GenerateFields } from '../../components/checkout/CheckoutInput';
import CheckoutWrapper from '../../components/checkout/CheckoutWrapper';
import { CheckoutContext, FlowsOptions, PaymentOptions, SourceOptions } from '../../store/CheckoutContext';
import {
  CHECKOUT_FORM_DEFAULT_STATE,
  CHECKOUT_FORM_DEMO_DEFAULT_STATE,
  CHECKOUT_FORM_DEMO_CHALLENGE_SUCCESS_DEFAULT_STATE,
  CHECKOUT_FORM_DEMO_CHALLENGE_DECLINE_DEFAULT_STATE,
} from '../../helpers/checkout-constants';
import { useNavigate, useLocation } from 'react-router-dom';
import { eligibility } from '../../helpers/checkout-api';
import { v4 as uuidv4 } from 'uuid';

import './Checkout.css';
import { flowType } from '../../store/DemoContext';
import ModalWindow from '../../components/modal/ModalWindow';

interface Props {}

const Iframe: React.FC<Props> = () => {
  const { cart } = useContext(CartContext);

  const {
    environment,
    setLoading,
    redirect,
    iframeSize,
    paymentOptions,
    sourceOptions,
    flow: checkoutFlow,
  } = useContext(CheckoutContext);
  const location = useLocation();
  const navigate = useNavigate();

  const [orderId] = useState<string>(uuidv4());
  const [demoMessages, setDemoMessages] = useState<string[]>([]);
  const prevDemoRef = useRef(['']);

  const [isModalOpen, setModalOpen] = useState(false);
  const [modalText, setModalText] = useState('');

  const handleOpenModal = (text: string) => {
    setModalText(text);
    setModalOpen(true);
  };

  const [ip, setIp] = useState();

  const getIp = async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      setIp(data.ip);
    } catch (error) {
      console.error('Error fetching IP address:', error);
    }
  };

  // Run `getIP` function above just once when the page is rendered
  useEffect(() => {
    getIp();
  }, []);

  useEffect(() => {
    if (!cart || cart.length === 0) {
      if (window.location.pathname.includes('demo')) {
        // eslint-disable-next-line no-restricted-globals
        parent.postMessage({ message: 'redirect', url: `/demo` }, '*');
      } else {
        // eslint-disable-next-line no-restricted-globals
        parent.postMessage({ message: 'redirect', url: `/` }, '*');
      }
    }
  }, [cart]);

  const setDemoMessage = (message: string): void => {
    const messages = prevDemoRef.current;

    if (message.includes('Processing') && messages[messages.length - 1].includes('Processing')) {
      const lastMessage = `${messages[messages.length - 1]}.`;
      const newMessages = [...messages];
      newMessages.pop();
      prevDemoRef.current = [...newMessages, lastMessage];
      setDemoMessages([...newMessages, lastMessage]);
      window?.top?.localStorage.setItem('demoMessages', JSON.stringify([...newMessages, lastMessage]));
    } else if (
      !message.includes('Challenge') ||
      (!messages.some(el => el.includes('Challenge')) &&
        (location.pathname.includes('challenge-success') || location.pathname.includes('challenge-decline')))
    ) {
      const newMessages = [];
      const existingMessages: any = {};

      messages?.forEach(message => {
        if (!existingMessages[message]) {
          newMessages.push(message);
          existingMessages[message] = true;
        }
      });

      if (!existingMessages[message]) {
        newMessages.push(message);
        existingMessages[message] = true;
      }

      prevDemoRef.current = [...newMessages];
      setDemoMessages([...newMessages]);
      window?.top?.localStorage.setItem('demoMessages', JSON.stringify([...newMessages]));
    }
  };

  const getDefaultState = () => {
    if (!location.pathname.includes('demo')) {
      return CHECKOUT_FORM_DEFAULT_STATE;
    }

    if (location.pathname.includes('challenge-success') || location.pathname.includes('challenge-frictionless')) {
      return CHECKOUT_FORM_DEMO_CHALLENGE_SUCCESS_DEFAULT_STATE;
    }

    if (location.pathname.includes('challenge-decline')) {
      return CHECKOUT_FORM_DEMO_CHALLENGE_DECLINE_DEFAULT_STATE;
    }

    return CHECKOUT_FORM_DEMO_DEFAULT_STATE;
  };

  const [state, setState] = useState<{ [key: string]: any }>({
    transactionAmount:
      cart?.reduce((total, cartItem) => total + cartItem.quantity * cartItem.item.price, 0).toFixed(2) || 0,
    ...getDefaultState(),
    mid: localStorage.getItem('mid') || '',
    siteId: localStorage.getItem('siteId') || null,
  });

  useEffect(() => {
    setTimeout(() => {
      setDemoMessage('<span class="green">FlexFactor: sending customer fingerprint to Server.</span>');
    }, 200);
  }, []);

  useEffect(() => {
    let keys: { spreedlyKey: string; appKey: string; appSecret: string; mid?: string; siteId?: string } = getApiKeys();

    if (location.pathname.includes('demo') && (!keys.appKey || !keys.appSecret || !keys.spreedlyKey)) {
      keys = getDemoKeys();
    }

    if (keys?.appKey?.length > 0 || keys?.appSecret.length > 0 || keys?.spreedlyKey.length > 0) {
      setState({
        ...state,
        appKey: keys?.appKey,
        appSecret: keys?.appSecret,
        spreedlyKey: keys?.spreedlyKey,
        mid: keys?.mid,
        siteId: keys?.siteId,
      });
    }
  }, []);

  async function paymentHandler() {
    if (location.pathname === '/terminal') {
      console.log('location.pathname_1', location.pathname);
      state.sourceOptions = sourceOptions === SourceOptions.empty ? null : sourceOptions;
      let body = getPaymentBody({ ...state, paymentOptions: paymentOptions }, cart, environment, orderId);
      const evaluatePayload = state.evaluatePayload?.length > 0 ? JSON.parse(state.evaluatePayload) : {};

      if (state?.state) {
        // @ts-ignore
        body.billingInformation.state = state?.state;
      }

      if (state?.saveAddress) {
        // @ts-ignore
        body.billingInformation = { ...body.billingInformation };
      }

      localStorage.setItem('state', JSON.stringify(state));
      localStorage.setItem('cart', JSON.stringify(cart));
      // console.log('JSON.stringify(body)', JSON.stringify(body));

      // const transactionName = state.responseCode === 100 || state.responseCode === '100' ? 'eligibility' : 'evaluate';
      let transactionName = 'evaluate';

      if (state.useSale) {
        transactionName = 'sale';
      }

      if (state?.evaluatePayload?.length > 10) {
        body = JSON.parse(state.evaluatePayload);
      }
      eligibility(body, environment, transactionName, response => {
        setLoading(false);
        switch (response.status) {
          case 'APPROVED':
            handleOpenModal('Your order is approved');
            // navigate('/success');
            // eslint-disable-next-line no-restricted-globals
            parent.postMessage({ message: 'redirect', url: `/success` }, '*');
            break;
          case 'CHALLENGE':
            handleOpenModal('You will receive SMS with a challenge');
            // navigate('/mit-message');
            // eslint-disable-next-line no-restricted-globals
            parent.postMessage({ message: 'redirect', url: `/mit-message` }, '*');
            break;
          case 'DECLINE':
          default:
            handleOpenModal('Your order is declined');
            // navigate('/decline');
            // eslint-disable-next-line no-restricted-globals
            parent.postMessage({ message: 'redirect', url: `decline/?isTerminal=true` }, '*');
        }
      });
    } else if ((window as any).UIWidget) {
      console.log('location.pathname_2', location.pathname);
      setTimeout(() => {
        setDemoMessage('Sending request for transaction to Processor.');
      }, 200);
      setTimeout(() => {
        setDemoMessage("Waiting for Processor's answer...");
      }, 400);
      setTimeout(() => {
        setDemoMessage('Answer from Processor: transaction denied.');
      }, 600);

      state.sourceOptions = sourceOptions === SourceOptions.empty ? null : sourceOptions;
      let body = getPaymentBody({ ...state, paymentOptions: paymentOptions }, cart, environment, orderId, ip);
      const evaluatePayload = state.evaluatePayload?.length > 0 ? JSON.parse(state.evaluatePayload) : {};

      if (checkoutFlow !== FlowsOptions.withoutOrderId) {
        (window as any).fc.setOrderId({ externalOrderId: evaluatePayload?.OrderId || orderId });
      }

      if (state?.state) {
        // @ts-ignore
        body.billingInformation.state = state?.state;
      }

      if (state?.saveAddress) {
        // @ts-ignore
        body.billingInformation = { ...body.billingInformation };
      }

      localStorage.setItem('state', JSON.stringify(state));
      localStorage.setItem('cart', JSON.stringify(cart));

      // const transactionName = state.responseCode === 100 || state.responseCode === '100' ? 'eligibility' : 'evaluate';
      let transactionName = 'evaluate';

      if (state.useSale) {
        transactionName = 'sale';
      }

      if (state?.evaluatePayload?.length > 10) {
        body = JSON.parse(state.evaluatePayload);
      }
      if (paymentOptions === PaymentOptions.mit) {
        // {
        //   SubscriptionId = Guid.NewGuid().ToString(),
        //     Interval = "monthly",
        //     Price = evaluateRequest.Transaction.Amount, //.ToString(),
        //     Currency = evaluateRequest.Transaction.Currency
        // };

        // @ts-ignore
        body.Subscription = {
          PaymentNumber: state.paymentNumber?.length > 0 ? state.paymentNumber : null,
          SubscriptionId: state.subscriptionId?.length > 0 ? state.subscriptionId : uuidv4(),
          Interval: state.subscriptionInterval,
          Price: body.transaction.amount,
          Currency: body.transaction.currency,
        };

        if (state.withEvaluate) {
          await eligibility(body, environment, transactionName);
        } else {
          console.log('body', JSON.stringify(body));
        }

        // navigate('/mit');
        // eslint-disable-next-line no-restricted-globals
        parent.postMessage({ message: 'redirect', url: `/mit-message` }, '*');
        return;
      } else {
        if (state.withEvaluate) {
          const response = await eligibility(body, environment, transactionName);
        } else {
          console.log('body', JSON.stringify(body));
        }
      }

      // if (state.responseCode === 100 || state.responseCode === '100') {
      //   setTimeout(() => {
      //     setLoading(false);
      //   }, 2000);
      //   return;
      // }

      const urls: any = {};
      ['successUrl', 'rejectUrl', 'cancelUrl'].forEach(el => {
        if (state[el] && state[el]?.length > 10) {
          // @ts-ignore
          urls[el] = state[el];
        }
      });
      const flow: { ConsentFlowConfiguration?: number } = {};

      if (location.pathname.includes('demo')) {
        flow.ConsentFlowConfiguration = 1;
        urls.successUrl = `${process.env.PUBLIC_URL}/demo/success`;
        urls.rejectUrl = `${process.env.PUBLIC_URL}/demo/decline`;
      }

      if (location.pathname === '/demo/iframe/consent/') {
        flow.ConsentFlowConfiguration = 2;
      }

      const initObject = {
        externalOrderId: body.OrderId,
        ...urls,
        ...flow,
        // @ts-ignore
        isCypress: !!window?.Cypress,
      };

      if (redirect === 'modal') {
        initObject.demoCallback = setDemoMessage;
      }

      if (redirect !== 'modal') {
        initObject.container = 'iframe-container';
        initObject.size = iframeSize;
      }

      if (
        location.pathname === '/demo/iframe/challenge-frictionless/' ||
        location.pathname === '/demo/iframe/frictionless/'
      ) {
        initObject.ConsentFlowConfiguration = 3;
        initObject.closeLoader = () => setLoading(false);
        // initObject.openLoader = () => setLoading(true);
      }

      if (state.closeLoaderCallback) {
        initObject.closeLoader = () => setLoading(false);
      }

      if (state.openLoaderCallback) {
        initObject.openLoader = () => setLoading(true);
      }

      setTimeout(() => {
        if (
          location.pathname !== '/demo/iframe/challenge-frictionless/' &&
          location.pathname !== '/demo/iframe/frictionless/' &&
          (!state.closeLoaderCallback || state.openLoaderCallback)
        ) {
          setLoading(false);
        }
      }, 2000);
      setTimeout(() => {
        if (redirect === 'new_tab') {
          const href = `/iframe-desktop?orderId=${orderId}&iframeSize=${iframeSize}`;
          window?.open(href, '_blank');
        } else if (redirect === 'same_page') {
          navigate(`/iframe-desktop`, { ...initObject });
        } else if (state.isAsynchronous && checkoutFlow !== FlowsOptions.withoutOrderId) {
          (window as any).UIWidget.InitUi(initObject);
        }
      }, 2200);

      // const options = {
      //   method: 'POST',
      //   // mode: 'cors',
      //   headers: {
      //     'Content-Type': 'application/json',
      //     'Access-Control-Allow-Origin': '*',
      //   },
      //   body: JSON.stringify(body),
      //   // credentials: 'include',
      // };

      // @ts-ignore
      // fetch(url, options)
      //   .then(res => res.json())
      //   .then(res => {
      //     if (res.statusCode !== null && res.statusCode !== 200 && res.statusCode !== 201) {
      //       // @ts-ignore
      //       const errors = { code: res?.statusCode || 'Unknown', message: res?.message || 'Payment error' };
      //       setPaymentErrors(errors);
      //       setLoading(false);
      //     } else {
      //       setLoading(false);
      //       if (res.transmitId) {
      //         if (window.location.pathname === '/iframe/') {
      //           // eslint-disable-next-line no-restricted-globals
      //           parent.postMessage('success', '*');
      //         } else {
      //           navigate('/success');
      //         }
      //         return;
      //       }
      //       switch (res.result) {
      //         case 'NOT_ELIGIBLE':
      //         case 'DECLINED':
      //         case 'ORDER_DECLINED':
      //           console.log('window.location.pathname++++', window.location.pathname);
      //           if (window.location.pathname.includes('/demo/iframe/')) {
      //             // eslint-disable-next-line no-restricted-globals
      //             parent.postMessage('decline-iframe', '*');
      //           } else if (window.location.pathname === '/iframe/') {
      //             // eslint-disable-next-line no-restricted-globals
      //             parent.postMessage('decline', '*');
      //           } else {
      //             navigate('/decline');
      //           }
      //           // window &&
      //           //   window.top &&
      //           //   window.top.location &&
      //           //   (window.top.location.href = `${window.location.origin}/decline`);
      //           break;
      //         default:
      //           const urls: any = {};
      //           ['successUrl', 'rejectUrl', 'cancelUrl'].forEach(el => {
      //             if (state[el] && state[el]?.length > 10) {
      //               // @ts-ignore
      //               urls[el] = state[el];
      //             }
      //           });
      //           const flow: { ConsentFlowConfiguration?: number } = {};
      //
      //           if (location.pathname.includes('demo')) {
      //             flow.ConsentFlowConfiguration = 2;
      //             urls.successUrl = `${process.env.PUBLIC_URL}/demo/success`;
      //             urls.rejectUrl = `${process.env.PUBLIC_URL}/demo/decline`;
      //
      //             // process.env.PUBLIC_URL
      //           }
      //
      //           if (location.pathname === '/demo/iframe/no-consent/') {
      //             flow.ConsentFlowConfiguration = 1;
      //           }
      //
      //           (window as any).UIWidget.InitUi({ ...res, externalOrderId: body.OrderId, ...urls, ...flow });
      //       }
      //     }
      //   })
      //   .catch(e => {
      //     const errors = { code: e.statusCode || 'Unknown', message: e.message || 'Payment error' };
      //     setPaymentErrors(errors);
      //     setLoading(false);
      //     // window &&
      //     //   window.top &&
      //     //   window.top.location &&
      //     //   (window.top.location.href = `${window.location.origin}/decline`);
      //   });
    } else {
      console.log('location.pathname_3', location.pathname);
    }
  }

  return (
    <CheckoutWrapper
      paymentElement={
        <>
          <GenerateFields
            state={state}
            setState={setState}
            fieldName={'paymentMethod'}
            showDemoConsole={window.location.pathname.includes('demo')}
            demoMessages={demoMessages}
          />
          <ModalWindow isOpen={isModalOpen} onClose={() => setModalOpen(false)}>
            <p>{modalText}</p>
          </ModalWindow>
        </>
      }
      validationSchema={VALIDATION_SCHEMA}
      paymentHandler={paymentHandler}
      state={state}
      setState={setState}
    />
  );
};

export default Iframe;

//                         var privateKey =
//                             @"-----BEGIN OPENSSH PRIVATE KEY-----
// b3BlbnNzaC1rZXktdjEAAAAACmFlczI1Ni1jdHIAAAAGYmNyeXB0AAAAGAAAABBFKTmPnn
// DCu/vnqhACP4g/AAAAGAAAAAEAAAAzAAAAC3NzaC1lZDI1NTE5AAAAIAXMJPfCiJL3BPNA
// y6duzDQl1a7Kev0HR9QVVvm2w5pUAAAAsCdFtu1CdlYzCy8sBctZAC23w9r+IA99wFvjRj
// Xrnh8eQSWn/XE+EMBirjs3p9GecX/w0uUBgaaL0E4mFFnLlWowETlE5uMb2yR7+lbSJ5FX
// sf5SGrGo8GngBRaLFlQP0mlogNfFRyIGKnIq5EsDZleABCvT8xj8c9kWpDPHbHlNSuSHu+
// RyU6Jpc/voo4ti5RQsAfcOShAPaCd+WauQpARhRrnd/g2czXeGgpmT7sP9
// -----END OPENSSH PRIVATE KEY-----";
//
// var privateKey = @"-----BEGIN OPENSSH PRIVATE KEY-----
// b3BlbnNzaC1rZXktdjEAAAAABG5vbmUAAAAEbm9uZQAAAAAAAAABAAABFwAAAAdzc2gtcn
// NhAAAAAwEAAQAAAQEA1p++0YJnUgoLaHzhsHD9kr/PmmE9GyeQjziy6jvHWPFOZow80wiq
// KqNjVhu6VrpmNnXXa5OKM5+BWhZMj+jGwxCba5+r/UnFCULBhvqH6sK5zbDr0lcCQoTMYn
// 0sMkalGFcRxJPA/m3urZRScYsaGCfUQCChmuzOozgwCt/n3s+gkLd9Ucp5fFFfrn180pOS
// 4Hww89pOaX3I+JCFavgDDR+UyzD/xVQoKPIsaXFyky1mWUlgPpNi3ZEiSs0PaghNwMx39P
// 5i+qUmvOkuXEfjbbIH8AQ3ZlogFD/4OJWKTH73RCcLNKHsU95f8B18menTMSc59DryMppX
// NpMXkNn2bwAAA9CXv58+l7+fPgAAAAdzc2gtcnNhAAABAQDWn77RgmdSCgtofOGwcP2Sv8
// +aYT0bJ5CPOLLqO8dY8U5mjDzTCKoqo2NWG7pWumY2dddrk4ozn4FaFkyP6MbDEJtrn6v9
// ScUJQsGG+ofqwrnNsOvSVwJChMxifSwyRqUYVxHEk8D+be6tlFJxixoYJ9RAIKGa7M6jOD
// AK3+fez6CQt31Rynl8UV+ufXzSk5LgfDDz2k5pfcj4kIVq+AMNH5TLMP/FVCgo8ixpcXKT
// LWZZSWA+k2LdkSJKzQ9qCE3AzHf0/mL6pSa86S5cR+NtsgfwBDdmWiAUP/g4lYpMfvdEJw
// s0oexT3l/wHXyZ6dMxJzn0OvIymlc2kxeQ2fZvAAAAAwEAAQAAAQAtC6wmFuOOkXMhhAxu
// VtixwzZUZZJIr74kgTbz+IX3gfX9ifN+K3RjrJU9zmYGq+qmBYeoGXhJK3IfsUxya93DtS
// mZphKX/7KuJeeuZ8i9Gg+bP0XPiNl9kpe/GBx9lNwzxpqBEq3P+My+e8Ht+M8KQNnId9DT
// Tl50XWef8Kye07ytoWEW1MFNQvZLs7Q/yDA/XRXrGNnn84HrnkJ5NmT8NsKe+eQ7H0SeVn
// 4KBc9tDNiDLKiO7S7n+3Jtv/VgOkOdr+vnL8LZof1IujvjzmjQesbCxJ0ksVbk3zBuK3AC
// +0JGw/R0EsR+Al0cHVz/8eUKkMWWNSpb3ofBuMncacwhAAAAgE7m9WihhwXBps+u38wQ2c
// oXAuuy6YHKUMl7ip3bb+CMQmmP3++NhlAiZoSq9Uvt8UIR7OyzWjkKFW//0OSBjrnmRolU
// RuvhgPC+AGJxmVdbn5gJM4IjIkHHA10n0JzCptnDp9n809t0zTrryujPnMTi6TS5RN5VwC
// u9HNC1eTbqAAAAgQDtVc4nKYsoK9siChbTJliS4NiuiyQ/PVl+eJWmw8NfjjnEm62qk/K+
// chHYdPjvdo107IHt+x8XQUzJBhWCyJ/rEwrPbRfdZdGsdbYb5lkuuh+BMG6piXuBm6kxTp
// xIcijRLrF9dsFqyUNpqrFcFKQBBRPkXY/0jGYg7KpAecjI0QAAAIEA54C0BSNH7qemxNxA
// /oNeygiotyG7RTlBTwBlNrHo3i9NKlzgy6K7vSze3oalyQ5dBkm2ReIMBMp4+WD2P7TAcG
// CtNkX8L09jz4rS4Hg8FKByjCd3VLOP2Dfk5vABl36rlsaGHR+euQa1eyXUeqZiaIs4dhWe
// +dVPvsx6IO+6mz8AAAAaZ3V5QGd1eXMtTWFjQm9vay1Qcm8ubG9jYWwB
// -----END OPENSSH PRIVATE KEY-----";
